import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { http } from '@/lib/api';
import {
  Bell, X, CheckCheck, Trash2, ClipboardList, MessageCircleHeart, BookOpen,
  Trophy, Sparkles, HandHeart, Heart, ArrowRight, BellOff,
} from 'lucide-react';

const iconFor = (type) => {
  if (type === 'checkin') return ClipboardList;
  if (type === 'coach') return MessageCircleHeart;
  if (type === 'lesson' || type === 'learn') return BookOpen;
  if (type === 'achievement' || type === 'milestone') return Trophy;
  if (type === 'weekly_review' || type === 'review') return Sparkles;
  if (type === 'support') return HandHeart;
  if (type === 'therapy') return Heart;
  return Bell;
};

const relativeTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diffSec < 60) return 'Just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

/** Derive a friendly action label from the target link path. */
const actionLabelFor = (link) => {
  if (!link) return null;
  const p = link.toLowerCase();
  if (p.includes('/checkin')) return 'Start Wellness Check-In';
  if (p.includes('/support/coach') || p.includes('/support-coach')) return 'Talk to Support Coach';
  if (p.includes('/coach')) return 'Talk to Coach';
  if (p.includes('/journal')) return 'Open Journal';
  if (p.includes('/learn')) return 'Start Lesson';
  if (p.includes('/plans')) return 'View Growth Plan';
  if (p.includes('/progress')) return 'View Progress';
  if (p.includes('/support-library') || p.includes('/support/library')) return 'Open Library';
  if (p.includes('/support-home')) return 'Open Support Home';
  if (p.includes('/support-crisis') || p.includes('/crisis')) return 'View Crisis Resources';
  if (p.includes('/support')) return 'View Support';
  if (p.includes('/therapists')) return 'Find a Therapist';
  if (p.includes('/subscription')) return 'View Membership';
  if (p.includes('/resources')) return 'Browse Resources';
  if (p.includes('/help')) return 'Learn How';
  if (p.includes('/roadmap')) return 'View Roadmap';
  return 'View Recommendation';
};

/* ---------- Notification Row (with swipe) ---------- */

function NotificationRow({ n, onOpen, onDismiss, onMarkRead }) {
  const Icon = iconFor(n.type);
  const rel = relativeTime(n.created_at);
  const cta = actionLabelFor(n.link);
  const isUnread = !n.read;

  // Swipe state
  const [dragX, setDragX] = useState(0);

  const handleDragEnd = (_e, info) => {
    const dx = info.offset.x;
    if (dx < -110) {
      // Swipe left → dismiss
      onDismiss(n.id);
    } else if (dx > 110 && isUnread) {
      // Swipe right → mark read
      onMarkRead(n.id);
    }
    setDragX(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -80, transition: { duration: 0.18 } }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className="relative"
      data-testid={`notification-row-${n.id}`}
    >
      {/* Swipe background */}
      <div
        aria-hidden
        className={`absolute inset-0 rounded-[20px] flex items-center justify-between px-6 pointer-events-none z-0 transition-colors duration-150 ${
          dragX > 20 && isUnread ? 'bg-emerald-500/85' : dragX < -20 ? 'bg-red-500/85' : ''
        }`}
      >
        <span
          className={`text-white text-xs font-semibold uppercase tracking-widest inline-flex items-center gap-2 transition-opacity ${
            dragX > 20 && isUnread ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <CheckCheck size={14}/> Mark read
        </span>
        <span
          className={`text-white text-xs font-semibold uppercase tracking-widest inline-flex items-center gap-2 transition-opacity ${
            dragX < -20 ? 'opacity-100' : 'opacity-0'
          }`}
        >
          Dismiss <Trash2 size={14}/>
        </span>
      </div>

      <motion.button
        drag="x"
        dragConstraints={{ left: -160, right: 160 }}
        dragElastic={0.12}
        onDrag={(_e, info) => setDragX(info.offset.x)}
        onDragEnd={handleDragEnd}
        whileHover={{ y: -1 }}
        onClick={(e) => {
          // Ignore click if swipe was significant
          if (Math.abs(dragX) > 8) { e.preventDefault(); return; }
          onOpen(n);
        }}
        aria-label={`${n.title}. ${n.body || ''} ${rel ? `Received ${rel}.` : ''}${isUnread ? ' Unread.' : ''}`}
        className={`relative z-10 w-full text-left rounded-[20px] transition-shadow duration-200 ease-out cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-gold ${
          isUnread ? 'shadow-[0_2px_10px_rgba(11,25,44,0.05)] hover:shadow-[0_10px_28px_rgba(11,25,44,0.10)]'
                   : 'shadow-[0_1px_4px_rgba(11,25,44,0.03)] hover:shadow-[0_8px_22px_rgba(11,25,44,0.08)]'
        }`}
        style={{
          backgroundColor: isUnread ? '#FFF9EE' : '#FFFFFF',
          border: `1px solid ${isUnread ? 'rgba(197,160,89,0.28)' : 'rgba(11,25,44,0.06)'}`,
          touchAction: 'pan-y',
        }}
        data-testid={`notification-card-${n.id}`}
        data-unread={isUnread ? 'true' : 'false'}
      >
        <div className="flex items-start gap-4 p-5">
          {/* Icon */}
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ backgroundColor: isUnread ? 'rgba(197,160,89,0.18)' : '#F4F1EA' }}
          >
            <Icon size={18} strokeWidth={1.75} className={isUnread ? 'text-gold' : 'text-navy/60'}/>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <p
                className="break-words leading-snug"
                style={{
                  fontSize: '16px',
                  fontWeight: isUnread ? 650 : 600,
                  color: isUnread ? '#111827' : 'rgba(17,24,39,0.72)',
                  letterSpacing: '-0.005em',
                }}
              >
                {n.title}
              </p>
              {isUnread && (
                <span
                  className="rounded-full shrink-0 mt-1.5"
                  style={{ backgroundColor: '#C5A059', width: 10, height: 10, boxShadow: '0 0 0 3px rgba(197,160,89,0.18)' }}
                  aria-label="Unread"
                />
              )}
            </div>

            {n.body && (
              <p
                className="mt-2 break-words"
                style={{
                  fontSize: '15px',
                  color: '#374151',
                  lineHeight: 1.55,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {n.body}
              </p>
            )}

            <div className="flex items-center justify-between gap-3 mt-3">
              <span style={{ fontSize: '13px', fontWeight: 500, color: '#6B7280', letterSpacing: '0.01em' }}>
                {rel}
              </span>
              {cta && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-150"
                  style={{
                    backgroundColor: isUnread ? '#0B192C' : 'rgba(11,25,44,0.06)',
                    color: isUnread ? '#FFFFFF' : '#0B192C',
                  }}
                >
                  {cta} <ArrowRight size={12}/>
                </span>
              )}
            </div>
          </div>
        </div>
      </motion.button>
    </motion.div>
  );
}

/* ---------- Main NotificationBell ---------- */

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const nav = useNavigate();
  const panelRef = useRef(null);

  const load = () => http.get('/notifications').then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Keyboard: ESC closes, focus trap start
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    // Focus the panel for screen readers
    setTimeout(() => panelRef.current?.focus(), 50);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const markRead = async (nid) => {
    // Optimistic update
    setData(d => ({
      ...d,
      items: d.items.map(i => i.id === nid ? { ...i, read: true } : i),
      unread: Math.max(0, d.unread - (d.items.find(i => i.id === nid)?.read ? 0 : 1)),
    }));
    try { await http.post(`/notifications/${nid}/read`); } catch { load(); }
  };
  const markAll = async () => {
    setData(d => ({ ...d, items: d.items.map(i => ({ ...i, read: true })), unread: 0 }));
    try { await http.post('/notifications/read-all'); } catch { load(); }
  };
  const dismissOne = async (nid) => {
    const target = data.items.find(i => i.id === nid);
    const wasUnread = target && !target.read;
    setData(d => ({
      ...d,
      items: d.items.filter(i => i.id !== nid),
      unread: Math.max(0, d.unread - (wasUnread ? 1 : 0)),
    }));
    try { await http.delete(`/notifications/${nid}`); } catch { load(); }
  };
  const clearAll = async () => {
    if (!window.confirm('Delete ALL notifications? This cannot be undone.')) return;
    setData({ items: [], unread: 0 });
    try { await http.delete('/notifications/clear-all'); } catch { load(); }
  };

  const openNotification = (n) => {
    // Mark read + navigate + close
    if (!n.read) markRead(n.id);
    if (n.link) nav(n.link);
    setOpen(false);
  };

  const hasAny = data.items.length > 0;
  const headerCount = useMemo(() => (
    data.unread > 0
      ? `${data.unread} unread`
      : hasAny ? `${data.items.length} notification${data.items.length === 1 ? '' : 's'}` : 'All caught up'
  ), [data.unread, data.items.length, hasAny]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="notification-bell"
        aria-label="Notifications"
        title="Notifications"
        className="relative w-10 h-10 rounded-full bg-white border border-navy/10 flex items-center justify-center hover:bg-beige transition-smooth"
      >
        <Bell size={16} className="text-navy" />
        {data.unread > 0 && (
          <span
            data-testid="notification-badge"
            aria-label={`${data.unread} unread notifications`}
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-gold text-white text-[10px] font-semibold rounded-full flex items-center justify-center"
          >
            {data.unread > 9 ? '9+' : data.unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 flex justify-end"
            style={{ zIndex: 100, backgroundColor: 'rgba(11, 25, 44, 0.55)' }}
            onClick={() => setOpen(false)}
            data-testid="notification-overlay"
          >
            <motion.aside
              ref={panelRef}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-labelledby="notification-heading"
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full sm:w-[440px] h-screen flex flex-col outline-none"
              style={{
                backgroundColor: '#FFFFFF',
                boxShadow: '-24px 0 60px -12px rgba(11, 25, 44, 0.25)',
                zIndex: 101,
              }}
              data-testid="notification-panel"
            >
              {/* Header */}
              <div
                className="px-7 pt-7 pb-5 flex items-start justify-between gap-4 shrink-0"
                style={{ borderBottom: '1px solid rgba(11,25,44,0.06)' }}
              >
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-gold font-semibold">Inbox</p>
                  <h2
                    id="notification-heading"
                    className="font-heading mt-1"
                    style={{ fontSize: '28px', color: '#111827', letterSpacing: '-0.015em' }}
                  >
                    Notifications
                  </h2>
                  <p className="mt-1.5" style={{ fontSize: '14px', color: '#6B7280', fontWeight: 500 }}>
                    {headerCount}
                  </p>
                </div>
                <button
                  data-testid="notification-close"
                  onClick={() => setOpen(false)}
                  aria-label="Close notifications"
                  className="w-10 h-10 rounded-full flex items-center justify-center text-navy/60 hover:text-navy hover:bg-beige transition-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                >
                  <X size={20}/>
                </button>
              </div>

              {/* Toolbar */}
              {hasAny && (
                <div
                  className="px-7 py-3 flex items-center justify-end gap-5 shrink-0"
                  style={{ borderBottom: '1px solid rgba(11,25,44,0.06)' }}
                >
                  {data.unread > 0 && (
                    <button
                      data-testid="notification-mark-all"
                      onClick={markAll}
                      className="inline-flex items-center gap-1.5 text-navy hover:text-gold whitespace-nowrap font-medium transition-colors duration-150"
                      style={{ fontSize: '13px' }}
                    >
                      <CheckCheck size={14}/> Mark all read
                    </button>
                  )}
                  <button
                    data-testid="notification-clear-all"
                    onClick={clearAll}
                    className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-800 whitespace-nowrap font-medium transition-colors duration-150"
                    style={{ fontSize: '13px' }}
                  >
                    <Trash2 size={13}/> Clear all
                  </button>
                </div>
              )}

              {/* Body */}
              <div
                className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-3"
                style={{ backgroundColor: '#FAFAF7' }}
              >
                {!hasAny ? (
                  <EmptyState/>
                ) : (
                  <AnimatePresence initial={false}>
                    {data.items.map((n) => (
                      <NotificationRow
                        key={n.id}
                        n={n}
                        onOpen={openNotification}
                        onDismiss={dismissOne}
                        onMarkRead={markRead}
                      />
                    ))}
                  </AnimatePresence>
                )}
              </div>

              {/* Footer hint (only when items exist) */}
              {hasAny && (
                <p className="px-7 py-3 text-center shrink-0" style={{ fontSize: '11px', color: '#9CA3AF', borderTop: '1px solid rgba(11,25,44,0.05)' }}>
                  Swipe left to dismiss · swipe right to mark as read
                </p>
              )}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function EmptyState() {
  return (
    <div className="text-center pt-16 pb-12 px-6" data-testid="notification-empty">
      {/* Illustration: layered concentric circles with a soft bell */}
      <div className="relative w-28 h-28 mx-auto mb-6">
        <div
          className="absolute inset-0 rounded-full"
          style={{ backgroundColor: 'rgba(197,160,89,0.08)' }}
        />
        <div
          className="absolute rounded-full"
          style={{
            top: '10%', left: '10%', right: '10%', bottom: '10%',
            backgroundColor: 'rgba(197,160,89,0.14)',
          }}
        />
        <div
          className="absolute rounded-full flex items-center justify-center"
          style={{
            top: '22%', left: '22%', right: '22%', bottom: '22%',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 12px 30px rgba(11,25,44,0.10)',
          }}
        >
          <BellOff size={26} className="text-navy/40" strokeWidth={1.4}/>
        </div>
      </div>
      <p className="font-heading" style={{ fontSize: '22px', color: '#111827', letterSpacing: '-0.015em' }}>
        You&rsquo;re all caught up.
      </p>
      <p className="mt-2.5 max-w-xs mx-auto" style={{ fontSize: '15px', color: '#6B7280', lineHeight: 1.55 }}>
        We&rsquo;ll let you know when something important happens.
      </p>
    </div>
  );
}
